<script setup lang="ts">
import { ClickOutside as vClickOutside } from 'element-plus';

import { useAppStore } from '@/store/app';
import { useUserStore } from '@/store/user';
import { login } from '@/utils/helper/auth';
import { LANGUAGE_REGION_CONFIG } from '@/constants';
import { useCartStore } from '@/store/cart';

const cartStore = useCartStore();

let popoverVisible = ref(false);

const { width } = useWindowSize();
const isDesktop = computed(() => {
	return width.value >= 1024;
});

const userStore = useUserStore();
const appStore = useAppStore();
const router = useRouter();

const isShowUserMenu = computed(() => appStore.getUserMenuState);
const isShowHeaderMenu = computed(() => appStore.getHeaderMenuState);
const isLoggedIn = computed(() => userStore.getLoggedIn);

const userLanguage = computed(() => {
	return appStore.getLanguage;
});

const routePath = computed(() => router.currentRoute.value.path);

const toggleUserMenu = () => {
	const _isShowUserMenu = isShowUserMenu.value;
	appStore.updateUserMenuState(!_isShowUserMenu);
};

const toggleHeaderMenu = () => {
	const _isShowHeaderMenu = isShowHeaderMenu.value;
	appStore.updateHeaderMenuState(!_isShowHeaderMenu);
};

watch(isDesktop, (value) => {
	if (value) {
		appStore.updateUserMenuState(false);
		appStore.updateHeaderMenuState(false);
	}
});

const hidePopover = () => {
	popoverVisible.value = false;
};

const handleLogin = () => {
	login();
};

const handleClearCart = () => {
	cartStore.resetCart();
};
</script>
<template>
	<div class="navbar-wrapper">
		<div class="header-container">
			<div class="logo-container">
				<div v-if="!isDesktop" class="hamburger-menu" @click="toggleHeaderMenu">
					<NuxtIcon name="hamburger-menu" :class="['icon-menu', { 'is-close': isShowHeaderMenu }]"></NuxtIcon>
				</div>
				<NuxtLink to="/">
					<img src="/images/vnggames-card-logo.png" alt="VNGGames Card Logo" />
				</NuxtLink>
			</div>
			<div class="content">
				<template v-if="isDesktop">
					<div class="link-group" @click="handleClearCart">
						<NuxtLink
							id="buy-card-link"
							to="/products"
							class="menu-item text-normal"
							:class="{ 'is-active': routePath.includes('products') }"
						>
							{{ $t('buyCards') }}
						</NuxtLink>

						<NuxtLink
							id="user-guide-link"
							:to="LANGUAGE_REGION_CONFIG[userLanguage.value].userGuideRoute"
							class="menu-item text-normal"
							:class="{ 'is-active': routePath.includes('user-guide') }"
							>{{ $t('userGuide.title') }}</NuxtLink
						>

						<NuxtLink
							id="faq-link"
							:to="LANGUAGE_REGION_CONFIG[userLanguage.value].faqRoute"
							class="menu-item text-normal"
							:class="{ 'is-active': routePath.includes('faq') }"
							>{{ $t('faq.title') }}</NuxtLink
						>
					</div>
					<select-language></select-language>
					<el-popover
						v-if="isLoggedIn"
						placement="bottom"
						:width="360"
						trigger="hover"
						:disabled="!isDesktop"
						:visible="popoverVisible"
					>
						<template #reference>
							<div
								v-click-outside="hidePopover"
								class="flex items-center"
								@click="popoverVisible = !popoverVisible"
							>
								<Avatar></Avatar>
							</div>
						</template>
						<div class="nav-menu">
							<user-menu @menu-click="hidePopover"></user-menu>
						</div>
					</el-popover>

					<div v-else class="flex items-center">
						<el-tooltip class="box-item" effect="dark" :content="$t('login')">
							<Avatar class="mr-2" @click="handleLogin"></Avatar>
						</el-tooltip>
					</div>
				</template>
				<div v-else class="mobile flex justify-center items-center">
					<Avatar @click="toggleUserMenu"></Avatar>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
@import '@/assets/scss/components/header.scss';
</style>
